import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NEWSLETTER_COPY } from '../ui/newsletter-form/newsletter.copy';

/** Espera tras un 429 si `Retry-After` no se puede leer. */
export const NEWSLETTER_RETRY_AFTER_FALLBACK_S = 60;

/** 422: formato del correo. El mensaje es el de la API (`error.email[0]`) o el local. */
export class NewsletterValidationError extends Error {
  constructor(message: string = NEWSLETTER_COPY.INVALID_EMAIL) {
    super(message);
    this.name = 'NewsletterValidationError';
  }
}

/** 429 del limitador `newsletter`: el formulario espera `retryAfterSeconds` antes de reintentar. */
export class NewsletterRateLimitError extends Error {
  constructor(readonly retryAfterSeconds: number) {
    super(NEWSLETTER_COPY.RATE_LIMITED);
    this.name = 'NewsletterRateLimitError';
  }
}

export interface NewsletterSubscribeResult {
  message: string;
}

interface NewsletterSubscribePayload {
  email: string;
  website?: string;
}

/**
 * Segundos de `Retry-After` (entero o fecha HTTP); `null` si no se lee. Copia del
 * parser de `article-comment-form.component.ts`: el blog no se importa desde fuera
 * (`tools/check-blog-imports.mjs`).
 */
export function retryAfterSeconds(header: string | null): number | null {
  if (header === null) return null;
  const value = header.trim();
  if (/^\d+$/.test(value)) {
    const seconds = Number(value);
    return seconds > 0 ? seconds : null;
  }
  const at = Date.parse(value);
  if (Number.isNaN(at)) return null;
  const seconds = Math.ceil((at - Date.now()) / 1000);
  return seconds > 0 ? seconds : null;
}

/** Primer mensaje de `error.email` del 422 de la API (`{"error": {"email": [...]}, "code": 422}`). */
function validationMessage(body: unknown): string | null {
  const errors = (body as { error?: unknown } | null)?.error;
  if (typeof errors !== 'object' || errors === null) return null;
  const email = (errors as { email?: unknown }).email;
  const first = Array.isArray(email) ? email[0] : email;
  return typeof first === 'string' && first.trim() !== '' ? first : null;
}

/**
 * Cliente del endpoint público de suscripción al newsletter (spec de la API §9.1).
 * La API responde 202 con el mismo cuerpo en TODOS los casos de éxito (nuevo,
 * pendiente, confirmado, honeypot), así que no se mira el status: cualquier 2xx es
 * éxito. 422 → `NewsletterValidationError`; 429 → `NewsletterRateLimitError`
 * con los segundos de `Retry-After` (expuesta en CORS); el resto se propaga tal cual.
 * El token Bearer lo adjunta el `authInterceptor` automáticamente.
 */
@Injectable({ providedIn: 'root' })
export class NewsletterService {
  private readonly http = inject(HttpClient);
  private readonly endpoint = `${environment.apiUrl}/newsletter-subscribers/subscribe`;

  /** `website` es el honeypot: solo viaja si trae algo (la API responde 202 igual). */
  subscribe(email: string, website = ''): Observable<NewsletterSubscribeResult> {
    const payload: NewsletterSubscribePayload = { email };
    if (website !== '') payload.website = website;

    return this.http.post<{ data?: unknown }>(this.endpoint, payload).pipe(
      map((body) => ({ message: typeof body?.data === 'string' ? body.data : '' })),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 422) {
            return throwError(
              () => new NewsletterValidationError(validationMessage(err.error) ?? undefined),
            );
          }
          if (err.status === 429) {
            const seconds =
              retryAfterSeconds(err.headers.get('Retry-After')) ?? NEWSLETTER_RETRY_AFTER_FALLBACK_S;
            return throwError(() => new NewsletterRateLimitError(seconds));
          }
        }
        return throwError(() => err);
      }),
    );
  }
}
